const apiUrl = process.env.REACT_APP_BACKEND_URL;
const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

const toError = err => (err instanceof Error ? err : new Error(err?.message || String(err)));

const handleResponse = async res => {
  if (!res.ok) {
    const error = new Error(`HTTP error! status: ${res.status}`);
    error.statusCode = res.status;
    throw error;
  }
  return await res.json();
};

export const getSensorData = async (fields, bucket, timeframeInMS) => {
  const requestBody = {
    fields: fields,
    timeframe: timeframeInMS,
    bucket: bucket,
    userTimeZone: userTimeZone,
  };

  const requestOptions = {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestBody),
  };

  try {
    const res = await fetch(`${apiUrl}data-explorer`, requestOptions);
    return await handleResponse(res);
  } catch (err) {
    console.error('getSensorData error:', err);
    throw toError(err);
  }
};

export const getLatestData = async (fields, bucket) => {
  const requestBody = {
    fields: fields,
    bucket: bucket,
  };

  const requestOptions = {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestBody),
  };

  try {
    const res = await fetch(`${apiUrl}data-explorer/last`, requestOptions);
    return await handleResponse(res);
  } catch (err) {
    console.error('getLatestData error:', err);
    throw toError(err);
  }
};

export const getLatestDataFromMultipleSensors = async (fields, buckets) => {
  const requestBody = {
    fields: fields,
    buckets: buckets,
  };

  const requestOptions = {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestBody),
  };

  try {
    const res = await fetch(`${apiUrl}data-explorer/last`, requestOptions);
    return await handleResponse(res);
  } catch (err) {
    console.error('getLatestDataFromMultipleSensors error:', err);
    throw toError(err);
  }
};

export const getAlerts = async (bucket, timeframeInMS, userTimeZone = 'Asia/Jerusalem', forPage = null) => {
  const requestBody = {
    timeframe: timeframeInMS,
    bucket: bucket,
    userTimeZone: userTimeZone,
    forPage: forPage,
  };

  const requestOptions = {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestBody),
  };

  try {
    const res = await fetch(`${apiUrl}data-explorer/alerts`, requestOptions);
    return await handleResponse(res);
  } catch (err) {
    console.error('getAlerts error:', err);
    throw toError(err);
  }
};

// export const getAlertsFromMultipleSensors = async (buckets, timeframeInMS) => {
//   const requestBody = {
//     timeframe: timeframeInMS,
//     buckets: buckets,
//   };

//   const requestOptions = {
//     method: 'POST',
//     credentials: 'include',
//     headers: {
//       'Content-Type': 'application/json',
//     },
//     body: JSON.stringify(requestBody),
//   };

//   try {
//     const res = await fetch(`${apiUrl}data-explorer/alerts`, requestOptions);
//     return await handleResponse(res);
//   } catch (err) {
//     console.error('getAlertsFromMultipleSensors error:', err);
//     throw toError(err);
//   }
// };

export const getSummary = async (buckets, timeframeInMS, timeJumpInMS, field) => {
  const requestBody = {
    timeframe: timeframeInMS,
    timeJump: timeJumpInMS,
    buckets: buckets,
    field: field,
  };

  const requestOptions = {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestBody),
  };

  try {
    const res = await fetch(`${apiUrl}data-explorer/summary`, requestOptions);
    return await handleResponse(res);
  } catch (err) {
    console.error('getSummary error:', err);
    throw toError(err);
  }
};

export const getRangeData = async (fields, bucket, timeframeInMS, userTimeZone, includeRawRecords = false) => {
  const requestBody = {
    fields: fields,
    bucket: bucket,
    timeframe: timeframeInMS,
    userTimeZone: userTimeZone,
    includeRawRecords,
  };

  const requestOptions = {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestBody),
  };

  try {
    const res = await fetch(`${apiUrl}data-explorer/`, requestOptions);
    return await handleResponse(res);
  } catch (err) {
    console.error('getRangeData error:', err);
    throw toError(err);
  }
};

export const getRangeDataOfType = async (
  measurementType,
  building_id,
  timeRange,
  device_ids = null,
  displayMode,
  floor,
  area,
) => {
  //detect timeZone
  const requestBody = {
    measurementType: measurementType,
    building_id: building_id,
    timeRange: timeRange,
    userTimeZone: userTimeZone,
    ...(device_ids && device_ids.length > 0 && { device_ids: device_ids }),
    ...(displayMode && { displayMode }),
    ...(floor !== undefined && { floor }),
    ...(area !== undefined && { area }),
  };

  const requestOptions = {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-Timezone': userTimeZone, // Also TZ in header for consistency
    },
    body: JSON.stringify(requestBody),
  };

  try {
    const res = await fetch(`${apiUrl}data-explorer/heatmap/${measurementType}`, requestOptions);
    return await handleResponse(res);
  } catch (err) {
    console.error('getRangeDataOfType error:', err);
    throw toError(err);
  }
};

export const getSensorsAvgsCurrData = async (building_id, userTimeZone, device_ids = null) => {
  const requestBody = {
    building_id: building_id,
    userTimeZone: userTimeZone,
    ...(device_ids && device_ids.length > 0 && { device_ids: device_ids }),
  };

  const requestOptions = {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestBody),
  };

  try {
    const res = await fetch(`${apiUrl}data-explorer/current-avgs`, requestOptions);
    return await handleResponse(res);
  } catch (err) {
    console.error('getSensorsAvgsCurrData error:', err);
    alert('No data available for the selected sensors');
    throw toError(err);
  }
};

export const getOcpAvgs = async (datesObj, building_id, device_ids = null, userTimeZone) => {
  const requestBody = {
    datesObj: datesObj,
    building_id: building_id,
    ...(device_ids && device_ids.length > 0 && { device_ids: device_ids }),
    userTimeZone: userTimeZone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
  };

  const requestOptions = {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestBody),
  };

  try {
    const res = await fetch(`${apiUrl}data-explorer/ocp-avgs`, requestOptions);
    return await handleResponse(res);
  } catch (err) {
    console.error('getOcpAvgs error:', err);
    alert('No data available for the selected sensors');
    throw toError(err);
  }
};

export const saveOcpReport = async reportData => {
  const requestOptions = {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(reportData),
  };

  try {
    const res = await fetch(`${apiUrl}ocp-reports`, requestOptions);
    return await handleResponse(res);
  } catch (err) {
    console.error('saveOcpReport error:', err);
    throw toError(err);
  }
};

//working
export const getRealTimeData = async (building_id, floor_number = null, device_ids = null) => {
  const requestBody = {
    building_id: building_id,
    ...(floor_number && floor_number !== 'all' && { floor_number: floor_number }),
    ...(device_ids && device_ids.length > 0 && { device_ids: device_ids }),
  };

  const requestOptions = {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestBody),
  };

  try {
    const res = await fetch(`${apiUrl}data-explorer/real-time-data`, requestOptions);
    return await handleResponse(res);
  } catch (err) {
    console.error('getRealTimeData error:', err);
    throw toError(err);
  }
};

export const getEfficiancyRangeDataOfType = async (forHomePage, measurementType, building_id, timeRange, device_ids = null) => {
  const requestBody = {
    forHomePage: forHomePage,
    measurementType: measurementType,
    building_id: building_id,
    timeRange: timeRange,
    userTimeZone: userTimeZone,
    ...(device_ids && device_ids.length > 0 && { device_ids: device_ids }),
  };

  const requestOptions = {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestBody),
  };

  try {
    const res = await fetch(`${apiUrl}data-explorer/efficiancy/${measurementType}`, requestOptions);
    return await handleResponse(res);
  } catch (err) {
    console.error('getEfficiancyRangeDataOfType error:', err);
    throw toError(err);
  }
};

/////// CONTROLLER DATA /////////

export const getControllerData = async (fields, controller_id, timeframeInMS, userTimeZone) => {
  const requestBody = {
    fields: fields,
    bucket: controller_id, // ← Change to "bucket" for general endpoint
    timeframe: timeframeInMS,
    userTimeZone: userTimeZone,
  };

  const requestOptions = {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestBody),
  };

  try {
    const res = await fetch(
      `${apiUrl}data-explorer`, // ← General endpoint (no /controller)
      requestOptions,
    );
    return await handleResponse(res);
  } catch (err) {
    console.error('getControllerData error:', err);
    throw toError(err);
  }
};

export const getControllerRangeData = async (fields, controller_id, timeframeInMS, userTimeZone, includeRawRecords = false) => {
  const requestBody = {
    fields: fields,
    controller_id: controller_id,
    timeframe: timeframeInMS,
    userTimeZone: userTimeZone,
    includeRawRecords,
  };

  const requestOptions = {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(requestBody),
  };

  try {
    const res = await fetch(`${apiUrl}data-explorer/controller`, requestOptions);
    const data = await res.json();

    if (!res.ok) {
      // ← Fix: was "response.ok" but variable is "res"
      // alert(
      //   'No data available for the selected controller for the selected time range. Try changing the time range.',
      // );
      const error = new Error(data.error || data.message || 'Controller data not found for selected time range');
      error.statusCode = res.status;
      throw error;
    }
    return data;
  } catch (err) {
    console.error('getControllerRangeData error:', err);
    throw toError(err);
  }
};

export const getLatestControllerData = async (fields, controllerId) => {
  const requestBody = {
    fields: fields,
    controllerId: controllerId,
  };

  const requestOptions = {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(requestBody),
  };

  const response = await fetch(`${apiUrl}data-explorer/controller/real-time`, requestOptions);

  const data = await response.json();

  // Check for HTTP errors or logical errors in the response
  if (!response.ok) {
    throw new Error(data.error || data.message || 'Manual control failed');
  }
  return data;
};

export const checkManualControlPasswordApi = async manualPassword => {
  const requestOptions = {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(manualPassword),
  };

  const response = await fetch(`${apiUrl}data-explorer/controller/manualcontrolpass`, requestOptions);

  const data = await response.json();

  // Check for HTTP errors or logical errors in the response
  if (!response.ok) {
    throw new Error(data.error || data.message || 'Manual control failed');
  }
  return data;
};

export const applySetPointsControllerValues = async (controller_id, device_mac, data) => {
  const requestBody = {
    controller_id: controller_id,
    device_mac: device_mac,
    data: data,
  };

  const requestOptions = {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(requestBody),
  };

  const response = await fetch(`${apiUrl}data-explorer/controller/applySetPoints`, requestOptions);

  const resData = await response.json();

  // Check for HTTP errors or logical errors in the response
  if (!response.ok) {
    throw new Error(resData.error || resData.message || 'Manual control failed');
  }
  return resData;
};

export const getDeviceInfo = async deviceId => {
  const requestOptions = {
    method: 'GET',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
  };

  try {
    const res = await fetch(`${apiUrl}data-explorer/device-info/${deviceId}`, requestOptions);
    const data = await res.json();

    if (!res.ok) {
      const error = new Error(data.error || data.message || 'Error fetching device info');
      error.statusCode = res.status;
      throw error;
    }
    return data;
  } catch (err) {
    console.error('getDeviceInfo error:', err);
    throw toError(err);
  }
};

/////// HOME PAGE ///////

export const getImpactDataAPI = async (building_id, floor_number, timeRange, userTimeZone, hvacEquipment = 'all') => {
  const requestBody = {
    building_id: building_id,
    floor_number: floor_number,
    hvacEquipment: hvacEquipment,
    timeRange: timeRange,
    userTimeZone: userTimeZone,
  };

  const requestOptions = {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestBody),
  };

  try {
    const res = await fetch(`${apiUrl}data-explorer/impact-data`, requestOptions);
    return await handleResponse(res);
  } catch (err) {
    console.error('getImpactDataAPI error:', err);
    throw toError(err);
  }
};

export const getHomePageInfo = async (building_id, floor_number, timeRange, columns, userTimeZone) => {
  const requestBody = {
    building_id: building_id,
    floor_number: floor_number,
    timeRange: timeRange,
    columns: columns,
    userTimeZone: userTimeZone,
  };

  const requestOptions = {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestBody),
  };

  try {
    const res = await fetch(`${apiUrl}data-explorer/home-page-info`, requestOptions);
    return await handleResponse(res);
  } catch (err) {
    console.error('getHomePageInfo error:', err);
    throw toError(err);
  }
};

export const getBuildingDemandSummary = async filters => {
  const requestOptions = {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(filters),
  };

  try {
    const res = await fetch(`${apiUrl}data-explorer/building-demand-summary`, requestOptions);
    return await handleResponse(res);
  } catch (err) {
    console.error('getBuildingDemandSummary error:', err);
    throw toError(err);
  }
};
